import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { stripeService } from '../../../services/stripeService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const PlanComparison = ({ plans, currentTier, onSelectPlan, userProfile }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Your data will be deleted on your renewal date.')) {
      return;
    }

    setIsCanceling(true);
    setCancelError(null);

    const { data, error: cancelError } = await stripeService?.cancelSubscription();

    if (cancelError) {
      setCancelError(cancelError);
      setIsCanceling(false);
      return;
    }

    // Refresh page to show updated status
    window.location?.reload();
  };

  const handleValidateCoupon = async () => {
    if (!couponCode?.trim()) {
      setCouponValidation(null);
      return;
    }

    setValidatingCoupon(true);
    const result = await stripeService?.validateCouponCode(couponCode);
    
    if (result?.valid) {
      // Check if this is the PILOT2MONTHS coupon (special handling)
      const isPilotCoupon = result?.coupon?.code === 'PILOT2MONTHS' && result?.coupon?.discount_type === 'free_trial';
      
      setCouponValidation({
        type: 'success',
        message: result?.coupon?.discount_type === 'free_trial' 
          ? `${result?.coupon?.trial_period_days} day free trial applied!`
          : 'Coupon code applied successfully!',
        coupon: result?.coupon,
        isPilotCoupon: isPilotCoupon
      });
      
      // If it's PILOT2MONTHS, activate immediately without Stripe
      if (isPilotCoupon) {
        await handlePilotActivation(result?.coupon);
      }
    } else {
      setCouponValidation({
        type: 'error',
        message: result?.error || 'Invalid coupon code'
      });
    }
    setValidatingCoupon(false);
  };

  const handlePilotActivation = async (coupon) => {
    try {
      console.log('Activating Pilot Mode for user:', user?.id);
      
      // Call the new service method to activate pilot mode
      const { data, error: activationError } = await stripeService?.activatePilotMode(user?.id, coupon?.id);
      
      if (activationError) {
        throw new Error(activationError);
      }
      
      // Show success message and reload to reflect new status
      setCouponValidation({
        type: 'success',
        message: '🚀 Pilot Mode activated! You now have 60 days of Pro access. Redirecting...',
        coupon: coupon,
        isPilotCoupon: true
      });
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        window.location.href = '/agent-dashboard?pilot_activated=true';
      }, 2000);
      
    } catch (error) {
      console.error('Pilot activation error:', error);
      setCouponValidation({
        type: 'error',
        message: error?.message || 'Failed to activate Pilot Mode'
      });
    }
  };

  const handleUpgrade = async (plan) => {
    if (plan?.tier === 'free' || plan?.tier === currentTier) return;

    setLoading(plan?.id);
    setError(null);

    try {
      console.log('\n=== CHECKOUT FLOW START ===');
      console.log('1. Plan Selection:', {
        planId: plan?.id,
        planName: plan?.name,
        planTier: plan?.tier,
        planPrice: plan?.price,
        stripePriceId: plan?.stripe_price_id // Log but don't use
      });

      // CRITICAL VALIDATION: Ensure we're using UUID, not Stripe price ID
      if (!plan?.id) {
        console.error('❌ CRITICAL: plan.id is missing!');
        throw new Error('Invalid plan data. Please refresh the page.');
      }

      if (plan?.id?.startsWith('price_')) {
        console.error('❌ CRITICAL: plan.id contains Stripe price ID instead of UUID!');
        console.error('  - plan.id:', plan?.id);
        console.error('  - This should be a UUID from subscription_plans.id');
        throw new Error('Invalid plan configuration. Please contact support.');
      }

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(plan?.id)) {
        console.error('❌ CRITICAL: plan.id is not a valid UUID!');
        console.error('  - plan.id:', plan?.id);
        throw new Error('Invalid plan identifier. Please refresh the page.');
      }

      console.log('✅ Plan ID validation passed - using UUID:', plan?.id);
      
      console.log('2. User Information:', {
        userId: user?.id,
        userEmail: user?.email
      });
      
      const appliedCoupon = couponValidation?.type === 'success' ? couponCode : null;
      console.log('3. Coupon Applied:', appliedCoupon || 'None');
      
      console.log('4. Calling stripeService.createCheckoutSession...');
      const startTime = Date.now();
      
      // Pass plan.id (UUID) to checkout - NEVER stripe_price_id
      const { data, error: checkoutError } = await stripeService?.createCheckoutSession(
        plan?.id, // UUID from subscription_plans.id
        user?.id,
        user?.email,
        appliedCoupon
      );
      
      const duration = Date.now() - startTime;
      console.log('5. Service call completed in', duration, 'ms');

      console.log('\n=== CHECKOUT RESPONSE ANALYSIS ===');
      console.log('6. Response received:', {
        hasData: !!data,
        hasError: !!checkoutError,
        dataType: typeof data,
        errorType: typeof checkoutError
      });
      
      if (checkoutError) {
        console.error('❌ Checkout error detected:', checkoutError);
        throw new Error(checkoutError);
      }
      
      console.log('7. Data object inspection:');
      console.log('  - data:', data);
      console.log('  - data.url:', data?.url);
      console.log('  - data.sessionId:', data?.sessionId);
      console.log('  - data keys:', data ? Object.keys(data) : 'N/A');
      console.log('  - Full data:', JSON.stringify(data, null, 2));

      console.log('\n8. Checkout URL Validation:');
      console.log('  - URL exists:', !!data?.url);
      console.log('  - URL type:', typeof data?.url);
      console.log('  - URL length:', data?.url?.length || 0);
      console.log('  - URL value:', data?.url || 'MISSING');
      console.log('  - URL is valid HTTPS:', data?.url?.startsWith('https://'));
      console.log('  - URL contains Stripe domain:', data?.url?.includes('stripe.com'));

      if (data?.url) {
        console.log('\n✅ Checkout URL received successfully');
        console.log('9. Preparing redirect to Stripe Checkout...');
        console.log('  - Target URL:', data?.url);
        console.log('  - Current location:', window.location?.href);
        console.log('  - Redirect method: window.location.href assignment');
        
        console.log('\n10. Triggering redirect NOW...');
        const redirectStartTime = Date.now();
        
        // Redirect to Stripe Checkout
        window.location.href = data?.url;
        
        // This code will likely not execute due to redirect, but log it anyway
        const redirectDuration = Date.now() - redirectStartTime;
        console.log('  - Redirect triggered in', redirectDuration, 'ms');
        console.log('  - If you see this message, redirect may have failed');
        console.log('  - Browser should now be navigating to Stripe...');
        console.log('=== CHECKOUT FLOW END (REDIRECT TRIGGERED) ===');
      } else {
        // Edge Function succeeded but didn't return a checkout URL
        console.error('\n❌ CRITICAL: No checkout URL in response!');
        console.error('  - Edge Function call succeeded (no error)');
        console.error('  - But data.url is missing or invalid');
        console.error('  - data.url value:', data?.url);
        console.error('  - Full response data:', JSON.stringify(data, null, 2));
        console.error('  - This indicates Edge Function returned success without URL');
        console.error('  - Possible causes:');
        console.error('    1. Edge Function did not return { url, sessionId } structure');
        console.error('    2. Stripe checkout.sessions.create failed silently');
        console.error('    3. Response serialization issue');
        console.error('=== CHECKOUT FLOW END (MISSING URL) ===');
        throw new Error('Checkout session created but no redirect URL received. Please try again.');
      }
    } catch (err) {
      console.error('\n❌ CHECKOUT FLOW ERROR:');
      console.error('  - Error message:', err?.message);
      console.error('  - Error type:', err?.name);
      console.error('  - Error stack:', err?.stack);
      console.error('=== CHECKOUT FLOW END (ERROR) ===');
      setError(err?.message || 'Failed to start checkout');
      setLoading(null);
    }
  };

  const getPlanFeatures = (plan) => {
    const features = plan?.features || {};
    return [
      {
        label: typeof features?.properties === 'string' ? features?.properties : `${features?.properties || 0} properties`,
        icon: 'Building2',
        included: true
      },
      {
        label: typeof features?.offers === 'string' ? features?.offers : `${features?.offers || 0} offers per property`,
        icon: 'FileText',
        included: true
      },
      {
        label: 'AI-powered contract analysis',
        icon: 'Sparkles',
        included: features?.ai_features || false
      },
      {
        label: 'Email offer reports',
        icon: 'Mail',
        included: features?.email_reports || false
      },
      {
        label: 'Priority support',
        icon: 'Headphones',
        included: features?.priority_support || false
      },
      {
        label: 'Advanced analytics',
        icon: 'BarChart3',
        included: features?.advanced_analytics || false
      }
    ];
  };

  const sortedPlans = [...(plans || [])]?.sort((a, b) => {
    // Sort by tier first (free < pro), then by price
    if (a?.tier === 'free') return -1;
    if (b?.tier === 'free') return 1;
    return (a?.price || 0) - (b?.price || 0);
  });

  // Filter to show only active plans and group Pro plans
  const activePlans = sortedPlans?.filter(p => p?.is_active !== false);
  const freePlan = activePlans?.find(p => p?.tier === 'free');
  const proMonthly = activePlans?.find(p => p?.tier === 'pro' && p?.billing_period === 'monthly');
  const proYearly = activePlans?.find(p => p?.tier === 'pro' && p?.billing_period === 'yearly');

  // Check if user is on Pro subscription with active status
  // EXCLUDE pilot mode users - they should see the 3 pricing tiles
  const isPilotMode = stripeService?.isPilotMode(userProfile);
  const isProUser = userProfile?.subscription_tier === 'pro' && 
                    (userProfile?.subscription_status === 'active' || userProfile?.subscription_status === 'trialing') &&
                    !isPilotMode; // Exclude pilot mode users
  const currentBillingPeriod = userProfile?.subscription_billing_period || 'monthly';
  const currentProPlan = plans?.find(p => p?.tier === 'pro' && p?.billing_period === currentBillingPeriod);
  const subscriptionEndDate = userProfile?.subscription_end_date;

  // CRITICAL: If user is on Pro, show ONLY single full-width tile (completely hide the 3 pricing tiles)
  if (isProUser) {
    // Find the user's current Pro plan, or construct one based on billing period
    let displayPlan = currentProPlan;
    
    // If we can't find the matching plan in the plans array, construct it
    if (!displayPlan) {
      const planName = currentBillingPeriod === 'yearly' ? 'Pro Yearly' : 'Pro Monthly';
      const price = currentBillingPeriod === 'yearly' ? 150 : 15;
      displayPlan = {
        name: planName,
        tier: 'pro',
        price: price,
        billing_period: currentBillingPeriod,
        features: {
          properties: 'unlimited',
          offers: 'unlimited',
          ai_features: true,
          email_reports: true,
          priority_support: true,
          advanced_analytics: true
        },
        property_limit: null,
        offer_limit: null,
        ai_features_enabled: true
      };
    }
    
    const features = getPlanFeatures(displayPlan);

    return (
      <div className="space-y-6">
        {/* Single Full-Width Pro Plan Tile */}
        <div className="bg-card border border-primary rounded-lg p-8 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Plan Info */}
            <div className="flex-grow">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Icon name="Crown" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">{displayPlan?.name}</h3>
                  <p className="text-sm text-muted-foreground">Your current subscription</p>
                </div>
              </div>
              
              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {features?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Icon
                      name={feature?.included ? 'Check' : 'X'}
                      size={18}
                      className={`flex-shrink-0 mt-0.5 ${
                        feature?.included ? 'text-success' : 'text-muted-foreground'
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        feature?.included ? 'text-foreground font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      {feature?.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Info */}
            <div className="flex-shrink-0 text-center md:text-right border-t md:border-t-0 md:border-l border-border pt-6 md:pt-0 md:pl-8">
              <div className="flex flex-col items-center md:items-end">
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-bold text-foreground">
                    {stripeService?.formatAmount(displayPlan?.price || 0)}
                  </span>
                  <span className="text-muted-foreground text-lg">/{displayPlan?.billing_period}</span>
                </div>
                {displayPlan?.billing_period === 'yearly' && (
                  <p className="text-sm text-success font-medium mb-4">Save $30 with yearly billing</p>
                )}
                <div className="bg-success/10 border border-success/20 rounded-lg px-4 py-2 flex items-center gap-2 mb-4">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm font-medium text-success">Active Subscription</span>
                </div>
                {subscriptionEndDate && (
                  <p className="text-xs text-muted-foreground mb-4">
                    Renews {stripeService?.formatDate(subscriptionEndDate)}
                  </p>
                )}
                {cancelError && (
                  <div className="mb-3 p-2 bg-destructive/10 border border-destructive rounded text-xs text-destructive">
                    {cancelError}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelSubscription}
                  disabled={isCanceling}
                  className="w-full"
                >
                  {isCanceling ? 'Processing...' : 'Cancel Subscription'}
                </Button>
              </div>
            </div>
          </div>

          {/* Renewal Info */}
          {subscriptionEndDate && (
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Your subscription will renew on{' '}
                <span className="font-medium text-foreground">
                  {new Date(subscriptionEndDate)?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </p>
            </div>
          )}

          {cancelError && (
            <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-destructive text-sm">{cancelError}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // For non-Pro users (Free or Pilot), show the 3 pricing tiles
  return (
    <div className="space-y-6">
      {/* Coupon Code Section */}
      <div className="bg-muted/50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Have a coupon code?</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="text"
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e?.target?.value?.toUpperCase())}
            className="flex-1"
          />
          <Button
            variant="outline"
            onClick={handleValidateCoupon}
            disabled={validatingCoupon || !couponCode?.trim()}
            iconName={validatingCoupon ? 'Loader2' : 'Tag'}
            iconClassName={validatingCoupon ? 'animate-spin' : ''}
            iconPosition="left"
          >
            {validatingCoupon ? 'Validating...' : 'Apply'}
          </Button>
        </div>
        {couponValidation && (
          <div
            className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
              couponValidation?.type === 'success' ?'bg-success/10 border border-success/20' :'bg-destructive/10 border border-destructive/20'
            }`}
          >
            <Icon
              name={couponValidation?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
              size={18}
              className={`flex-shrink-0 mt-0.5 ${
                couponValidation?.type === 'success' ? 'text-success' : 'text-destructive'
              }`}
            />
            <p
              className={`text-sm ${
                couponValidation?.type === 'success' ? 'text-success' : 'text-destructive'
              }`}
            >
              {couponValidation?.message}
            </p>
          </div>
        )}
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free Plan */}
        {freePlan && (
          <div className="bg-card border border-border rounded-lg p-6 flex flex-col">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">{freePlan?.name}</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold text-foreground">
                  {stripeService?.formatAmount(freePlan?.price || 0)}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">Perfect for trying out OfferSense</p>
            </div>

            <div className="flex-grow space-y-3 mb-6">
              {getPlanFeatures(freePlan)?.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Icon
                    name={feature?.included ? 'Check' : 'X'}
                    size={18}
                    className={`flex-shrink-0 mt-0.5 ${
                      feature?.included ? 'text-success' : 'text-muted-foreground'
                    }`}
                  />
                  <span
                    className={`text-sm ${
                      feature?.included ? 'text-foreground' : 'text-muted-foreground line-through'
                    }`}
                  >
                    {feature?.label}
                  </span>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              disabled
              fullWidth
            >
              Current Plan
            </Button>
          </div>
        )}

        {/* Pro Monthly Plan */}
        {proMonthly && (
          <div className="bg-card border-2 border-primary rounded-lg p-6 flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
              Most Popular
            </div>
            
            <div className="mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">{proMonthly?.name}</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold text-foreground">
                  {stripeService?.formatAmount(proMonthly?.price || 0)}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">Full access to all features</p>
            </div>

            <div className="flex-grow space-y-3 mb-6">
              {getPlanFeatures(proMonthly)?.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Icon
                    name={feature?.included ? 'Check' : 'X'}
                    size={18}
                    className={`flex-shrink-0 mt-0.5 ${
                      feature?.included ? 'text-success' : 'text-muted-foreground'
                    }`}
                  />
                  <span
                    className={`text-sm ${
                      feature?.included ? 'text-foreground font-medium' : 'text-muted-foreground line-through'
                    }`}
                  >
                    {feature?.label}
                  </span>
                </div>
              ))}
            </div>

            <Button
              variant="default"
              onClick={() => handleUpgrade(proMonthly)}
              disabled={loading === proMonthly?.id}
              iconName={loading === proMonthly?.id ? 'Loader2' : 'Crown'}
              iconClassName={loading === proMonthly?.id ? 'animate-spin' : ''}
              iconPosition="left"
              fullWidth
            >
              {loading === proMonthly?.id ? 'Processing...' : 'Upgrade to Pro'}
            </Button>
          </div>
        )}

        {/* Pro Yearly Plan */}
        {proYearly && (
          <div className="bg-card border border-border rounded-lg p-6 flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-success text-white px-3 py-1 rounded-full text-xs font-semibold">
              Save 17%
            </div>
            
            <div className="mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">{proYearly?.name}</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-foreground">
                  {stripeService?.formatAmount(proYearly?.price || 0)}
                </span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <p className="text-sm text-success font-medium mb-2">$12.50/month billed annually</p>
              <p className="text-sm text-muted-foreground">Best value for committed users</p>
            </div>

            <div className="flex-grow space-y-3 mb-6">
              {getPlanFeatures(proYearly)?.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Icon
                    name={feature?.included ? 'Check' : 'X'}
                    size={18}
                    className={`flex-shrink-0 mt-0.5 ${
                      feature?.included ? 'text-success' : 'text-muted-foreground'
                    }`}
                  />
                  <span
                    className={`text-sm ${
                      feature?.included ? 'text-foreground font-medium' : 'text-muted-foreground line-through'
                    }`}
                  >
                    {feature?.label}
                  </span>
                </div>
              ))}
            </div>

            <Button
              variant="default"
              onClick={() => handleUpgrade(proYearly)}
              disabled={loading === proYearly?.id}
              iconName={loading === proYearly?.id ? 'Loader2' : 'Crown'}
              iconClassName={loading === proYearly?.id ? 'animate-spin' : ''}
              iconPosition="left"
              fullWidth
            >
              {loading === proYearly?.id ? 'Processing...' : 'Upgrade to Pro'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanComparison;